import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { Responsive, WidthProvider } from 'react-grid-layout';
import {
  useBoolean,
  useInterval,
  useLifecycles,
  useUpdateEffect,
  useWindowSize,
} from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CircularProgress from '@mui/material/CircularProgress';
import LogoutIcon from '@mui/icons-material/Logout';

import AreaChart from '../components/dashboard/AreaChart';
import BubbleChart from '../components/dashboard/BubbleChart';
import PieChart from '../components/dashboard/PieChart';
import {
  getAllStatus,
  getInitialData,
  getStatusOfUsingRobots,
  getUsageByDetectedFace,
  getUsageByMostUsedBot,
  getUsageByRobot,
  getUsageByTime,
  getUsageByTopic,
  getUsagePurposeByBot,
  signIn,
  refresh,
} from '../modules/dashboard';
import { numberWithCommas } from '../common/util';
import PieTabs from '../components/dashboard/PieTabs';
import SupplyMap from '../components/dashboard/SupplyMap';
import SPSupplyTable from '../components/dashboard/SPSupplyTable';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const DragCardHeader = styled(Typography)(
  ({ theme }) => `
  margin: -1rem -1rem 1rem;
  padding: 0.25rem 1rem;
  background-color: ${theme.palette.secondary.main};
  color: ${theme.palette.secondary.contrastText};
  cursor: move;
`,
);
/* const InstitutionSelect = styled(Select)(
  ({ theme }) => `
  font-size: 2.5707rem;
  font-weight: bold;
  color: ${theme.palette.secondary.main};
  & .MuiSelect-select {
    line-height: 1.167;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-right: ${theme.spacing(3)};
  }
  & .MuiSelect-select input {
    border: none;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  & fieldset {
    border: none;
    padding: 0;
  }
  &	.MuiSelect-icon {
    color: ${theme.palette.secondary.main};
  }
`,
); */

const StatusCard = styled(Card)`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const CenteredBox = styled(Box)`
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
`;

const LAYOUT = {
  lg: [
    { w: 12, h: 2, x: 0, y: 0, i: '0', static: true, isDraggable: false },
    { w: 1, h: 3, x: 0, y: 2, i: '1', static: false },
    { w: 1, h: 3, x: 1, y: 2, i: '2', static: false },
    { w: 2, h: 3, x: 2, y: 2, i: '3', static: false },
    { w: 8, h: 10, x: 4, y: 2, i: '4', static: false },
    { w: 4, h: 7, x: 0, y: 5, i: '5', static: false },
    { w: 4, h: 9, x: 0, y: 11, i: '6', static: false },
    { w: 4, h: 9, x: 4, y: 11, i: '7', static: false },
    { w: 4, h: 9, x: 8, y: 11, i: '8', static: false },
  ],
  md: [
    { w: 10, h: 2, x: 0, y: 0, i: '0', static: true, isDraggable: false },
    { w: 1, h: 3, x: 0, y: 2, i: '1', static: false },
    { w: 1, h: 3, x: 1, y: 2, i: '2', static: false },
    { w: 2, h: 3, x: 2, y: 2, i: '3', static: false },
    { w: 6, h: 10, x: 4, y: 2, i: '4', static: false },
    { w: 4, h: 7, x: 0, y: 5, i: '5', static: false },
    { w: 4, h: 9, x: 0, y: 12, i: '6', static: false },
    { w: 3, h: 9, x: 4, y: 12, i: '7', static: false },
    { w: 3, h: 9, x: 7, y: 12, i: '8', static: false },
  ],
  sm: [
    { w: 6, h: 2, x: 0, y: 0, i: '0', static: true, isDraggable: false },
    { w: 3, h: 3, x: 0, y: 2, i: '1', static: false },
    { w: 3, h: 3, x: 3, y: 2, i: '2', static: false },
    { w: 6, h: 3, x: 0, y: 5, i: '3', static: false },
    { w: 6, h: 10, x: 0, y: 8, i: '4', static: false },
    { w: 6, h: 10, x: 0, y: 18, i: '5', static: false },
    { w: 6, h: 10, x: 0, y: 28, i: '6', static: false },
    { w: 6, h: 10, x: 0, y: 38, i: '7', static: false },
    { w: 6, h: 10, x: 0, y: 48, i: '8', static: false },
  ],
  xs: [
    { w: 4, h: 2, x: 0, y: 0, i: '0', static: true, isDraggable: false },
    { w: 2, h: 3, x: 0, y: 2, i: '1', static: false },
    { w: 2, h: 3, x: 2, y: 2, i: '2', static: false },
    { w: 4, h: 3, x: 0, y: 5, i: '3', static: false },
    { w: 4, h: 10, x: 0, y: 8, i: '4', static: false },
    { w: 4, h: 10, x: 0, y: 18, i: '5', static: false },
    { w: 4, h: 10, x: 0, y: 28, i: '6', static: false },
    { w: 4, h: 10, x: 0, y: 38, i: '7', static: false },
    { w: 4, h: 10, x: 0, y: 48, i: '8', static: false },
  ],
  xxs: [
    { w: 2, h: 2, x: 0, y: 0, i: '0', static: true, isDraggable: false },
    { w: 1, h: 2, x: 0, y: 2, i: '1', static: false },
    { w: 1, h: 2, x: 1, y: 2, i: '2', static: false },
    { w: 2, h: 2, x: 0, y: 3, i: '3', static: false },
    { w: 2, h: 10, x: 0, y: 5, i: '4', static: false },
    { w: 2, h: 10, x: 0, y: 15, i: '5', static: false },
    { w: 2, h: 10, x: 0, y: 25, i: '6', static: false },
    { w: 2, h: 10, x: 0, y: 35, i: '7', static: false },
    { w: 2, h: 10, x: 0, y: 45, i: '8', static: false },
  ],
};
const INIT_INST = { id: 'songpa', institution: '송파구청' };
const SPDashboardPage = ({ darkMode, setColorMode }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    loading: {
      list: listLoading,
      0: loading0,
      6: loading6,
      7: loading7,
      12: loading12,
    },
    data: { 6: data6, 7: data7, 12: data12 },
    robots,
    status,
    institution,
    token,
  } = useSelector((state) => state.dashboard);
  const { width } = useWindowSize();
  const borderColor = theme.palette.divider;
  const textColor = theme.palette.text.primary;
  const [type, setAreaType] = useState(0);
  const [isRunning, toggleIsRunning] = useBoolean(true);
  const [mounted, setMounted] = useState(false);
  const [userId, setId] = useState('');
  const [password, setPW] = useState('');
  const [bSignedIn, setSignIn] = useState(false);

  const handleAreaChartChange = (e, v) => {
    setAreaType(v);
    dispatch(getUsageByRobot(v ? 'month' : 'week'));
  };

  const getBreakPoint = () => {
    const idx = Object.values(theme.breakpoints.values).reduce(
      (val, cur, i) => (width > cur ? i : val),
      -1,
    );
    const bp = ['xxs', 'xs', 'sm', 'md', 'lg'];
    return bp[idx];
  };

  const handleSignOut = () => {
    dispatch(refresh(''));
    localStorage.removeItem('dashboardIn');
    setSignIn(false);
  };

  const CONTENT = [
    <Stack direction="row" justifyContent="space-between">
      <Stack
        direction={getBreakPoint() === 'xxs' ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <Typography variant="h3" color="secondary.main" sx={{ mr: 1 }}>
          {institution}
        </Typography>
        <Typography variant="h3"> 공급 파이보 대시보드</Typography>
      </Stack>
      <Stack direction="row">
        <IconButton onClick={handleSignOut} color="inherit">
          <LogoutIcon />
        </IconButton>
        <IconButton sx={{ ml: 1 }} onClick={setColorMode} color="inherit">
          {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Stack>
    </Stack>,
    <StatusCard variant="outlined">
      {loading0 && <CircularProgress color="secondary" />}
      {!loading0 && (
        <Typography variant="h3" color="secondary">
          {status.supply}
        </Typography>
      )}
      <Typography variant="h5">보급</Typography>
    </StatusCard>,
    <StatusCard variant="outlined">
      {loading0 && <CircularProgress color="secondary" />}
      {!loading0 && (
        <Typography variant="h3" color="secondary">
          {status.use}
        </Typography>
      )}
      <Typography variant="h5">사용중</Typography>
    </StatusCard>,
    <StatusCard variant="outlined">
      {loading0 && <CircularProgress color="secondary" />}
      {!loading0 && (
        <Typography variant="h3" color="secondary">
          {numberWithCommas(status.dialogs)}
        </Typography>
      )}
      <Typography variant="h5">대화</Typography>
    </StatusCard>,
    <Card variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 2rem)' }}>
        <DragCardHeader variant="h6">공급 현황</DragCardHeader>
        {loading0 && (
          <CenteredBox>
            <CircularProgress color="secondary" />
          </CenteredBox>
        )}
        {!loading0 && <SPSupplyTable />}
      </CardContent>
    </Card>,
    <Card variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 4.5rem)' }}>
        <DragCardHeader variant="h6">대화량</DragCardHeader>
        <PieTabs value={type} onChange={handleAreaChartChange} />
        {loading6 && (
          <CenteredBox>
            <CircularProgress color="secondary" />
          </CenteredBox>
        )}
        <AreaChart {...{ borderColor, textColor, data6 }} />
      </CardContent>
    </Card>,
    <Card variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 2rem)' }}>
        <DragCardHeader variant="h6">공급위치</DragCardHeader>
        <SupplyMap
          {...{
            loading: loading0,
            data: robots,
            useRobots: status.useRobots,
          }}
        />
      </CardContent>
    </Card>,
    <Card variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 2rem)' }}>
        <DragCardHeader variant="h6">사용량</DragCardHeader>
        {loading7 && (
          <CenteredBox>
            <CircularProgress color="secondary" />
          </CenteredBox>
        )}
        <BubbleChart {...{ borderColor, textColor, data7 }} />
      </CardContent>
    </Card>,
    <Card variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 2rem)' }}>
        <DragCardHeader variant="h6">주 사용 봇</DragCardHeader>
        {loading12 && (
          <CenteredBox>
            <CircularProgress color="secondary" />
          </CenteredBox>
        )}
        <PieChart {...{ borderColor, textColor, data12 }} />
      </CardContent>
    </Card>,
  ];

  const getLayout = (v) =>
    LAYOUT[v].map((item, i) => ({ ...item, content: CONTENT[i] }));

  // const handleLayoutChange = (layout, layoutList) => {
  //   console.log('layout', layout);
  //   console.log('layoutList', layoutList);
  // };

  const getClass = (v, fixed) => {
    const names = [];
    if (fixed) {
      names.push('static');
    }
    return names.join(' ');
  };

  useInterval(
    () => {
      dispatch(getInitialData(INIT_INST));
    },
    isRunning ? 15 * 60 * 1000 : null,
  );

  useInterval(
    () => {
      dispatch(getStatusOfUsingRobots());
    },
    bSignedIn && status.use === 0 ? 1000 : 5 * 60 * 1000,
  );

  useUpdateEffect(() => {
    if (robots.length) {
      dispatch(getAllStatus());
      dispatch(getUsageByRobot(type ? 'month' : 'week'));
      dispatch(getUsageByTime());
      dispatch(getUsageByMostUsedBot());
      dispatch(getUsagePurposeByBot());
      dispatch(getUsageByTopic());
      dispatch(getUsageByDetectedFace());
    }
  }, [robots]);

  useEffect(() => {
    if (token) {
      setSignIn(true);
      localStorage.setItem('dashboardIn', '송파구청');
      dispatch(getInitialData(INIT_INST));
    }
  }, [token]);

  useLifecycles(
    () => {
      setMounted(true);
      const saved = localStorage.getItem('dashboardIn');
      if (saved) {
        setSignIn(true);
        dispatch(getInitialData(INIT_INST));
      } else {
        setSignIn(false);
        localStorage.removeItem('dashboardIn');
      }
    },
    () => {
      setMounted(false);
      toggleIsRunning();
      setSignIn(false);
    },
  );

  const onChangeId = (event) => {
    setId(event.target.value);
  };

  const onChangePw = (event) => {
    setPW(event.target.value);
  };

  const handleSignIn = (event) => {
    if (event) event.preventDefault();
    if (userId !== 'songpa') {
      setId('');
      setPW('');
    } else if (userId && password) {
      dispatch(signIn({ userId, password }));
    }
  };

  const handleKeyUp = (e) => {
    const { key, keyCode } = e;
    if (key === 'Enter' || keyCode === 13) {
      handleSignIn();
    }
  };

  return (
    <>
      {listLoading && (
        <CenteredBox>
          <CircularProgress color="secondary" />
        </CenteredBox>
      )}
      {!(listLoading || bSignedIn) && (
        <Container
          sx={{ m: 'auto', height: '100vh', display: 'flex' }}
          maxWidth="xs"
        >
          <Card sx={{ m: 'auto', width: '100%' }}>
            <CardContent>
              <Stack
                sx={{ width: '100%', alignItems: 'center', padding: '16px 0' }}
                spacing={2}
              >
                <Typography variant="h6">
                  현황 조회를 하려면 로그인하세요.
                </Typography>
                <Stack
                  sx={{ width: '80%', alignSelf: 'center', padding: '16px 0' }}
                  spacing={2}
                >
                  <TextField
                    name="userId"
                    label="아이디"
                    helperText="아이디를 입력하세요."
                    onBlur={onChangeId}
                    onChange={onChangeId}
                    onKeyUp={handleKeyUp}
                    value={userId}
                    error={!userId}
                  />
                  <TextField
                    name="password"
                    type="password"
                    label="비밀번호"
                    helperText="비밀번호를 입력하세요."
                    onBlur={onChangePw}
                    onChange={onChangePw}
                    onKeyUp={handleKeyUp}
                    value={password}
                    error={!password}
                  />
                </Stack>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSignIn}
                  fullWidth
                >
                  로그인
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      )}
      {!listLoading && bSignedIn && (
        <div style={{ width: '100%', height: '100%', padding: '8px' }}>
          <ResponsiveReactGridLayout
            {...{
              className: 'layout',
              rowHeight: 30,
              cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 },
              // onLayoutChange: handleLayoutChange,
              initialLayout: getLayout(getBreakPoint()),
            }}
            layouts={{
              lg: getLayout('lg'),
              md: getLayout('md'),
              sm: getLayout('sm'),
              xs: getLayout('xs'),
              xxs: getLayout('xxs'),
            }}
            measureBeforeMount={false}
            preventCollision={false}
            useCSSTransforms={mounted}
            compactType={['md', 'lg'].includes(
              getBreakPoint() ? 'horizontal' : 'vertical',
            )}
          >
            {getLayout(getBreakPoint()).map(
              ({ w, h, x, y, i, static: fixed, content }) => {
                return (
                  <div
                    {...{
                      w,
                      h,
                      x,
                      y,
                      key: i,
                      className: getClass(i, fixed),
                    }}
                  >
                    {content}
                  </div>
                );
              },
            )}
          </ResponsiveReactGridLayout>
        </div>
      )}
    </>
  );
};

export default SPDashboardPage;
